import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';
import { setHeaderRuntimeVar } from '@theme/critical';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMobileDrawerPositioning();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.removeAttribute('open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Setup mobile drawer positioning to account for dynamic header height
   */
  #setupMobileDrawerPositioning() {
    // Only apply to mobile navigation drawer
    if (!this.classList.contains('header-drawer--mobile-nav')) return;

    // Update mobile drawer position when header height changes
    const updateMobileDrawerPosition = () => {
      // Get the actual mobile navigation element position
      const mobileNav = document.querySelector('.header__row--mobile-nav');
      if (mobileNav) {
        const mobileNavRect = mobileNav.getBoundingClientRect();
        const mobileDrawerTop = mobileNavRect.bottom;
        setHeaderRuntimeVar('--mobile-drawer-top', `${mobileDrawerTop}px`);
      } else {
        // Fallback to calculated values
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
        const mobileNavHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--mobile-nav-height')) || 60;
        const mobileDrawerTop = headerHeight + mobileNavHeight;
        setHeaderRuntimeVar('--mobile-drawer-top', `${mobileDrawerTop}px`);
      }
    };

    // Initial update
    updateMobileDrawerPosition();

    // Listen for header height changes
    const resizeObserver = new ResizeObserver(() => {
      updateMobileDrawerPosition();
    });

    // Observe the header element
    const header = document.querySelector('header-component');
    if (header) {
      resizeObserver.observe(header);
    }

    // Also observe the mobile navigation element
    const mobileNav = document.querySelector('.header__row--mobile-nav');
    if (mobileNav) {
      resizeObserver.observe(mobileNav);
    }

    // Listen for scroll events to update position when announcement bar hides/shows
    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        updateMobileDrawerPosition();
      }, 10);
    };

    document.addEventListener('scroll', handleScroll, { passive: true });

    // Fix toggle functionality for mobile drawer
    this.#setupMobileDrawerToggle();
  }

  /**
   * Setup mobile drawer toggle functionality
   */
  #setupMobileDrawerToggle() {
    const details = this.querySelector('details');
    const summary = this.querySelector('summary');
    const backdrop = this.querySelector('.menu-drawer__backdrop');

    if (!details || !summary) return;

    // Override the summary click to ensure proper toggle
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      console.log('Mobile drawer toggle clicked');
      
      if (details.hasAttribute('open')) {
        // Close the drawer
        this.close();
      } else {
        // Open the drawer
        this.open();
      }
    });

    // Setup backdrop click to close
    if (backdrop) {
      backdrop.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      });
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.classList.add('menu-drawer__animated-element--settled');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
